/**
 * Warden tier set: Coilguard Bulwark (crafting.md §5.4). `bonuses` target the rotation-rework
 * kit (docs/design/classes/warden.md); `interim` uses today's kit until those ids land.
 */
import { ABILITY, CLASS } from '@keystone/content';
import { TIER_SET_IDS, tierAdd, tierMult } from '../../forge.constants';
import { auraRef } from '../../transforms';
import type { TierSetDef } from '../../types';

const W = ABILITY.warden;
/** Rotation-rework ids (warden.md §2). */
const RW = {
  riposte: 'warden.riposte',
  aegisThrow: 'warden.aegis_throw',
  riposteProc: 'warden.opening', // the proc was renamed Opening in the lead pass
  bastion: 'warden.bastion',
} as const;
const SHED_SKIN_MARK = 'set.warden.shed_skin_mark';
const SHED_SKIN = 'set.warden.shed_skin';

export const WARDEN_SET: TierSetDef = {
  id: TIER_SET_IDS[CLASS.warden]!,
  name: 'Coilguard Bulwark',
  classId: CLASS.warden,
  pieceNames: { helm: 'Coilguard Greathelm', chest: 'Coilguard Breastplate', legs: 'Coilguard Legplates', gloves: 'Coilguard Gauntlets', boots: 'Coilguard Sabatons' },
  text: {
    2: { name: 'Coiled Riposte', description: 'Shield Slam has a 12% chance to grant Opening, and Riposte deals double damage.' },
    4: { name: 'Shed Skin', description: 'Bulwark Strike at 5 or more Bastion resets Shield Slam and leaves you at 2 Bastion.' },
  },
  bonuses: {
    2: [
      {
        op: 'addEffects',
        abilityId: W.shieldSlam,
        effects: [{ type: 'conditional', cond: { type: 'chance', p: tierAdd(0.12) }, then: [{ type: 'applyAura', to: 'self', aura: auraRef(RW.riposteProc) }] }],
      },
      { op: 'scaleAmount', abilityId: RW.riposte, effectType: 'damage', mult: tierMult(2) },
    ],
    4: [
      // Runs before Bulwark consumes Bastion: the burst, plus a marker read after the consume.
      {
        op: 'addEffects',
        abilityId: W.bulwarkStrike,
        position: 'start',
        effects: [
          {
            type: 'conditional',
            cond: { type: 'selfHasAura', auraId: RW.bastion, minStacks: 5 },
            then: [
              { type: 'modifyCooldown', abilityId: W.shieldSlam, reset: true },
              { type: 'applyAura', to: 'self', aura: { id: SHED_SKIN_MARK, name: 'Shed Skin', kind: 'buff', durationMs: 1000, stacking: 'refresh', flags: { hidden: true } } },
            ],
          },
        ],
      },
      {
        op: 'addEffects',
        abilityId: W.bulwarkStrike,
        effects: [
          {
            type: 'conditional',
            cond: { type: 'selfHasAura', auraId: SHED_SKIN_MARK },
            then: [
              { type: 'applyAura', to: 'self', stacks: 2, aura: auraRef(RW.bastion) },
              { type: 'removeAura', to: 'self', auraId: SHED_SKIN_MARK },
            ],
          },
        ],
      },
    ],
  },
  interim: {
    text: {
      2: { name: 'Coiled Riposte', description: 'Cleave Strike has a 25% chance to reset Shield Slam, and Shield Slam deals 15% more damage.' },
      4: { name: 'Shed Skin', description: 'Bulwark Strike resets Shield Slam, refunds 15 Resolve and grants Shed Skin: 10% less damage taken for 6 s.' },
    },
    bonuses: {
      2: [
        {
          op: 'addEffects',
          abilityId: W.cleaveStrike,
          effects: [{ type: 'conditional', cond: { type: 'chance', p: tierAdd(0.25) }, then: [{ type: 'modifyCooldown', abilityId: W.shieldSlam, reset: true }] }],
        },
        { op: 'scaleAmount', abilityId: W.shieldSlam, effectType: 'damage', mult: tierMult(1.15) },
      ],
      4: [
        {
          op: 'addEffects',
          abilityId: W.bulwarkStrike,
          effects: [
            { type: 'modifyCooldown', abilityId: W.shieldSlam, reset: true },
            { type: 'resource', to: 'self', resource: 'resolve', amount: Math.round(tierAdd(15)) },
            {
              type: 'applyAura',
              to: 'self',
              aura: { id: SHED_SKIN, name: 'Shed Skin', kind: 'buff', durationMs: 6000, stacking: 'refresh', mods: [{ stat: 'damageTakenPct', value: -tierAdd(0.1) }] },
            },
          ],
        },
      ],
    },
  },
};
